/**
 * #391 -- the payment line shown to a customer on their policy, per stage.
 *
 * The broker-side Status card dropped its stored payment column on purpose (it
 * could disagree with the payment records); the customer portal wants the fact
 * itself, so this is derived from the real payment rows plus the stage:
 *   - issued (or any later stage): "Payment Received" -- a policy is only
 *     issued once payment has gone through;
 *   - draft / pending issuance: from the payments actually recorded.
 */
export interface PaymentStageResult {
  label: string;
  /** Key understood by `getStatusBadgeVariant`. */
  badge: string;
}

const PRE_ISSUANCE = new Set(['draft', 'pending_payment', 'payment_received', 'pending_issuance']);

export function paymentStageStatus(
  policyStatus: string | undefined,
  payments: { payment_status: string }[] | undefined
): PaymentStageResult | null {
  if (!policyStatus) return null;
  const received = (payments ?? []).some((p) => p.payment_status === 'success');
  if (!PRE_ISSUANCE.has(policyStatus)) {
    // Cancelled before any money moved is not "received".
    if (policyStatus === 'cancelled' && !received) return null;
    return { label: 'Payment Received', badge: 'completed' };
  }
  if (received || policyStatus === 'payment_received') {
    return { label: 'Payment Received', badge: 'completed' };
  }
  if (policyStatus === 'draft' && (payments ?? []).length === 0) {
    return { label: 'Payment Not Started', badge: 'draft' };
  }
  return { label: 'Payment Pending', badge: 'pending' };
}

/**
 * #632 -- Payment History rows including a premium taken before the policy was
 * entered. An installment settled at issue (#422) has no `PolicyPayment`
 * receipt on purpose (no mode or reference was given), so the schedule said
 * "1 of 12 paid" while the history said "No payments recorded". The settled
 * installment is shown as a display-only row; one that already has a real
 * receipt (it carries that receipt's reference) is not repeated.
 */
export interface HistoryRow {
  id: string;
  amount: string;
  payment_date: string | null;
  payment_mode_label?: string;
  payment_status: string;
  note?: string;
}

export function paymentHistoryRows(
  payments: HistoryRow[] | undefined,
  schedule: {
    id: string;
    installment_number: number;
    status: string;
    paid_at: string | null;
    paid_amount: string | null;
    amount: string;
    payment_reference: string;
  }[] | undefined
): HistoryRow[] {
  const rows = [...(payments ?? [])];
  for (const inst of schedule ?? []) {
    if (inst.status !== 'paid' || inst.payment_reference) continue;
    rows.push({
      id: `installment-${inst.id}`,
      amount: inst.paid_amount ?? inst.amount,
      payment_date: inst.paid_at,
      payment_mode_label: '',
      payment_status: 'success',
      note: `Installment ${inst.installment_number}`,
    });
  }
  return rows;
}

/**
 * R2-F5 / #632 -- text of the empty Payment History card. "Payment confirmed at
 * issuance" is only true for a policy paid in one go. On an instalment policy a
 * settled first instalment already appears as its own row, so an empty history
 * there means nothing is paid yet and must not claim a confirmation.
 */
export function emptyHistoryMessage(
  policyStatus: string | undefined,
  rows: HistoryRow[] | undefined,
  isInstallmentPolicy: boolean
): string {
  if (!isInstallmentPolicy && paymentStageStatus(policyStatus, rows)?.label === 'Payment Received') {
    return 'Payment confirmed at issuance; no receipt entry is on file';
  }
  return 'No payments recorded';
}
